import { makeAutoObservable } from "mobx";
import { Transaction } from "../../types/Transactions";
import {
  combineDateAndTime,
  DEFAULT_TIME,
  nextAvailableTime,
  toDate,
  toLocalDateISOString,
  toLocalTime,
} from "../../utils/Dates";
import { ExtendedShareholderDTO } from "../../types/Shareholder";
import { FilterKeys, TransactionType } from "../../types/enums/TransactionType";
import { groupBy, identity, mapValues, max } from "lodash";

import { TMessage } from "../../i18n/tt";
import { useShareholdersStore, useTranslation } from "../../stores/RootStoreContext";
import { useMemo, useState } from "react";
import { filterKeysMap } from "../../types/const/TransactionType";
import { LocalDateFieldModel, RadioFieldModel, TimeFieldModel } from "../../common/commonForms";
import { ShareTypeCode } from "../../common/share";

export class TransactionDateAndTimeForm {
  transactionDate: LocalDateFieldModel;
  transactionTime = new TimeFieldModel({ required: true });

  constructor(
    readonly minDate: string,
    readonly options?: {
      value?: string;
      onChange?: (params: { date: string | undefined; time: string | undefined }) => void;
    },
    readonly maxDate?: string,
  ) {
    const onChange = () =>
      options?.onChange?.({
        date: this.transactionDate.validatedValue,
        time: this.transactionTime.validatedValue,
      });

    this.transactionDate = new LocalDateFieldModel({
      required: true,
      minDate: minDate,
      maxDate: maxDate,
      onChange,
      value: options?.value ? toLocalDateISOString(options?.value) : undefined,
    });
    this.transactionTime = new TimeFieldModel({
      required: true,
      onChange,

      value: options?.value ? toLocalTime(options?.value) : undefined,
    });
    makeAutoObservable(this);
  }

  setAndCommitTransactionDate(value: string) {
    this.setTransactionDate(value);
    this.transactionDate.commit();
  }

  setTransactionDate(value: string) {
    this.transactionDate.setInputValue(value);
    this.transactionTime.setAndCommitInputValue(nextAvailableTime(value, this.minDate));
  }

  get combinedDateAndTime(): string | undefined {
    if (this.transactionDate.validatedValue && this.transactionTime.validatedValue) {
      return combineDateAndTime(
        this.transactionDate.validatedValue,
        this.transactionTime.validatedValue,
      );
    }
  }

  get isValid() {
    return this.transactionDate.isValid && this.transactionTime.isValid;
  }

  get isSubmitEnabled() {
    if (!this.isValid) {
      return false;
    }
    const newDate = this.combinedDateAndTime;
    const oldDate = this.options?.value;
    return newDate && oldDate && toDate(newDate).getTime() !== toDate(oldDate).getTime();
  }

  validate() {
    this.transactionDate.validate();
    this.transactionTime.validate();
    const validatedValue = this.transactionDate.validatedValue;
    const minDateIso = toLocalDateISOString(this.minDate);
    if (validatedValue === minDateIso) {
      const time = toLocalTime(this.minDate);
      this.transactionTime.validateAfterTime(time);
    }
    if (this.maxDate) {
      const maxDateIso = toLocalDateISOString(this.maxDate);
      if (validatedValue === maxDateIso) {
        const maxTime = toLocalTime(this.maxDate);
        this.transactionTime.validateBeforeTime(maxTime);
      }
    }
    return this.isValid;
  }

  get isSet() {
    return (
      this.isValid && this.transactionDate.validatedValue && this.transactionTime.validatedValue
    );
  }
}

export class TransactionDateCardModel {
  transactionDate: LocalDateFieldModel;
  transactionTime: TimeFieldModel;
  data: { transactionLog: Transaction[] };
  info?: TMessage;
  error?: TMessage;
  isPrefilled = false;

  constructor({
    data,
    onChange,
    value,
  }: {
    data: { transactionLog: Transaction[] };
    onChange?: (date?: string, time?: string) => void;
    value?: string;
  }) {
    this.data = data;
    this.isPrefilled = !!value;

    this.transactionTime = new TimeFieldModel({
      value: value ? toLocalTime(value) : DEFAULT_TIME,
      required: true,
      onChange: (value) => {
        this.checkTransactionsAfter();
        onChange?.(this.transactionDate.validatedValue, value);
      },
    });
    this.transactionDate = new LocalDateFieldModel({
      value: value ? toLocalDateISOString(value) : undefined,
      required: true,
      onChange: (value) => {
        if (value) {
          this.checkTransactionsAfter();
          const timesByDate = groupBy(this.transactionDates, (date) => toLocalDateISOString(date));
          const maxTimeByDate = mapValues(timesByDate, max);
          const maxTime = maxTimeByDate[value];
          this.transactionTime.setAndCommitInputValue(
            maxTime ? nextAvailableTime(value, maxTime) : DEFAULT_TIME,
          );
        }
        onChange?.(value, this.transactionTime.validatedValue);
      },
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get transactionDates(): string[] {
    return this.data.transactionLog.map((t) => t.transactionDate);
  }

  checkTransactionsAfter() {
    const inputDate = this.combinedDateAndTime;
    if (!inputDate) {
      return;
    }
    const transactionDatesAfter = this.transactionDates.filter((date) => date > inputDate);
    this.info =
      transactionDatesAfter.length === 0
        ? undefined
        : {
            key: "transactions.transactions-after-found",
            params: {
              count: transactionDatesAfter.length,
              date: { type: "datetime", value: inputDate },
            },
          };
  }

  validate() {
    this.error = undefined;
    this.transactionDate.validate();
    this.transactionTime.validate();
    if (this.combinedDateAndTime && this.transactionDates.includes(this.combinedDateAndTime)) {
      this.error = { key: "transactions.existing-transaction-on-date" };
    }
    return this.isValid;
  }

  get isValid(): boolean {
    return this.error === undefined && this.transactionDate.isValid && this.transactionTime.isValid;
  }

  get combinedDateAndTime(): string | undefined {
    if (this.transactionDate.validatedValue && this.transactionTime.validatedValue) {
      return combineDateAndTime(
        this.transactionDate.validatedValue,
        this.transactionTime.validatedValue,
      );
    }
  }
}

export type NumberOfShares = {
  sharetypeCode: ShareTypeCode;
  numberOfShares: number;
};

export function totalNumberOfShares(shareholders: ExtendedShareholderDTO[]): NumberOfShares[] {
  const aggregatedCounts: { [key in ShareTypeCode]?: number } = {};
  shareholders.forEach((s) => {
    Object.entries(s.calculatedShares).forEach(([sharetypeCode, count]) => {
      if (!aggregatedCounts[sharetypeCode as ShareTypeCode]) {
        aggregatedCounts[sharetypeCode as ShareTypeCode] = 0;
      }
      aggregatedCounts[sharetypeCode as ShareTypeCode]! += count!;
    });
  });
  return Object.entries(aggregatedCounts).map(([sharetypeCode, numberOfShares]) => ({
    sharetypeCode: sharetypeCode as ShareTypeCode,
    numberOfShares,
  }));
}

export type DividendTransaction = TransactionType.PAY_DIVIDEND | TransactionType.GROUP_CONTRIBUTION;

export enum Resolution {
  POA = "PoA",
  GENERAL_MEETING = "generalMeeting",
}

export function createResolutionMode({
  transactionType,
  onChange,
}: {
  transactionType: DividendTransaction;
  onChange: (option: Resolution | undefined) => void;
}) {
  return new RadioFieldModel<Resolution | undefined>({
    required: true,
    value: undefined,
    options: [Resolution.POA, Resolution.GENERAL_MEETING],
    idFn: identity,
    labelFn: (option) =>
      option === Resolution.POA
        ? resolutionGeneralMeetingLabel[transactionType]
        : { key: "transactions.dividendTransaction.general-meeting" },
    onChange,
  });
}

const resolutionGeneralMeetingLabel: { [key in DividendTransaction]: TMessage } = {
  [TransactionType.PAY_DIVIDEND]: { key: "transactions.dividendTransaction.board-according-poa" },
  [TransactionType.GROUP_CONTRIBUTION]: {
    key: "transactions.dividendTransaction.power-of-attorney",
  },
};

export const useFilterTransactions = () => {
  const { transactionLog } = useShareholdersStore();
  const { t } = useTranslation();
  const [selectedTransactionFilter, setSelectedTransactionFilter] = useState<string>(
    FilterKeys.ALL_TRANSACTIONS,
  );

  const onFilter = (key: string) => {
    setSelectedTransactionFilter(key);
  };

  const filteredTransactionLog: Transaction[] = useMemo(
    () =>
      transactionLog.filter((transaction) =>
        filterKeysMap[selectedTransactionFilter as FilterKeys].some(
          (transactionType) => transactionType === transaction.transactionType,
        ),
      ),
    [transactionLog, selectedTransactionFilter],
  );

  type TransactionOptions = {
    value: FilterKeys;
    label: string;
  };

  const transactionOptions: TransactionOptions[] = [
    ...Object.values(FilterKeys).map<TransactionOptions>((filterValue) => ({
      value: filterValue,
      label: t(`transactions.${filterValue}`),
    })),
  ];

  return {
    selectedTransactionFilter: selectedTransactionFilter,
    onFilter,
    filteredTransactionLog,
    transactionOptions,
  };
};

export function dateBefore(date: string) {
  const dateMinusOneMs = new Date(toDate(date).getTime() - 1);
  return dateMinusOneMs.toISOString();
}
